import { useEffect, useRef } from "react";
import styles from "./MapWrapper.module.scss";

const ROUTES = [
  "M-40 210 C 180 40, 360 260, 560 170 S 880 20, 1180 140",
  "M 1280 -20 C 1380 180, 1520 260, 1680 210",
  "M-20 620 C 160 520, 340 740, 560 640 S 820 500, 1040 720",
  "M 980 820 C 1180 700, 1380 860, 1680 740",
  "M 220 40 C 280 280, 120 480, 80 760",
  "M 1480 80 C 1420 320, 1580 520, 1520 880",
  "M 640 40 C 720 300, 980 340, 1240 180",
  "M 40 820 C 280 760, 520 880, 760 800",
];

const DOTS: Array<{ x: number; y: number; r?: number; hub?: boolean }> = [
  { x: 120, y: 168, hub: true },
  { x: 268, y: 118, r: 1.6 },
  { x: 430, y: 210 },
  { x: 560, y: 170, hub: true },
  { x: 780, y: 86, r: 1.5 },
  { x: 980, y: 148, hub: true },
  { x: 1180, y: 140 },
  { x: 1360, y: 96, r: 1.4 },
  { x: 1520, y: 210, hub: true },
  { x: 80, y: 420, r: 1.5 },
  { x: 210, y: 560 },
  { x: 360, y: 690, r: 1.6 },
  { x: 560, y: 640, hub: true },
  { x: 820, y: 560, r: 1.4 },
  { x: 1040, y: 720, hub: true },
  { x: 1240, y: 760 },
  { x: 1480, y: 700, hub: true },
  { x: 1580, y: 430, r: 1.5 },
  { x: 1420, y: 320 },
  { x: 720, y: 300, r: 1.4 },
  { x: 980, y: 340 },
  { x: 220, y: 40, r: 1.5 },
  { x: 1480, y: 80 },
  { x: 40, y: 820, r: 1.5 },
  { x: 760, y: 800 },
];

const STARS: Array<{ x: number; y: number; r: number }> = [
  { x: 90, y: 60, r: 0.7 },
  { x: 190, y: 300, r: 0.5 },
  { x: 310, y: 40, r: 0.8 },
  { x: 390, y: 480, r: 0.5 },
  { x: 470, y: 90, r: 0.6 },
  { x: 610, y: 250, r: 0.5 },
  { x: 690, y: 520, r: 0.7 },
  { x: 840, y: 40, r: 0.5 },
  { x: 910, y: 610, r: 0.6 },
  { x: 1050, y: 200, r: 0.5 },
  { x: 1120, y: 470, r: 0.8 },
  { x: 1260, y: 60, r: 0.5 },
  { x: 1330, y: 540, r: 0.6 },
  { x: 1450, y: 160, r: 0.5 },
  { x: 1540, y: 620, r: 0.7 },
  { x: 70, y: 700, r: 0.5 },
  { x: 250, y: 840, r: 0.6 },
  { x: 500, y: 780, r: 0.5 },
  { x: 880, y: 860, r: 0.7 },
  { x: 1180, y: 840, r: 0.5 },
  { x: 1570, y: 860, r: 0.6 },
  { x: 740, y: 140, r: 0.5 },
  { x: 160, y: 500, r: 0.45 },
  { x: 1390, y: 400, r: 0.5 },
];

const PACKET_ROUTES = [0, 4];

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const GlobeBackdrop: RFC = () => {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const host = root?.parentElement;
    if (!root || !host || prefersReducedMotion()) return;

    const target = { x: 0.5, y: 0.5 };
    const current = { x: 0.5, y: 0.5 };
    let raf = 0;

    const apply = () => {
      root.style.setProperty("--pointer-x", current.x.toFixed(4));
      root.style.setProperty("--pointer-y", current.y.toFixed(4));
    };

    const tick = () => {
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;
      apply();
      if (Math.abs(target.x - current.x) < 0.001 && Math.abs(target.y - current.y) < 0.001) {
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    const start = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const onMove = (event: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      target.x = (event.clientX - rect.left) / rect.width;
      target.y = (event.clientY - rect.top) / rect.height;
      start();
    };

    const onLeave = () => {
      target.x = 0.5;
      target.y = 0.5;
      start();
    };

    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerleave", onLeave);
    apply();

    return () => {
      cancelAnimationFrame(raf);
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <div ref={rootRef} className={styles.backdrop} aria-hidden>
      <div className={styles.ambient} />
      <div className={styles.grid} />
      <div className={styles.cursorGlow} />

      <svg className={styles.backdropSvg} viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
        <g className={styles.stars} fill="currentColor">
          {STARS.map((star, index) => (
            <circle
              key={`${star.x}-${star.y}`}
              className={styles.star}
              cx={star.x}
              cy={star.y}
              r={star.r}
              style={{ animationDelay: `${index * -0.41}s` }}
            />
          ))}
        </g>

        <g className={styles.backdropRoutes} fill="none" stroke="currentColor">
          {ROUTES.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>

        <g className={styles.routeFlows} fill="none" stroke="currentColor">
          {ROUTES.map((d, index) => (
            <path
              key={`flow-${d}`}
              className={styles.routeFlow}
              d={d}
              pathLength={1}
              style={{ animationDelay: `${index * -1.7}s` }}
            />
          ))}
        </g>

        <g className={styles.packets} fill="currentColor">
          {PACKET_ROUTES.map((routeIndex, index) => (
            <circle key={ROUTES[routeIndex]} className={styles.packet} r={1.4}>
              <animateMotion
                dur={`${18 + index * 4}s`}
                repeatCount="indefinite"
                path={ROUTES[routeIndex]}
                begin={`${index * -4}s`}
              />
            </circle>
          ))}
        </g>

        <g className={styles.backdropDots} fill="currentColor">
          {DOTS.map((dot, index) => {
            const radius = dot.r ?? 2.2;
            return (
              <g key={`${dot.x}-${dot.y}`} transform={`translate(${dot.x} ${dot.y})`}>
                {dot.hub && (
                  <circle className={styles.pulseRing} r={radius} style={{ animationDelay: `${index * -0.55}s` }} />
                )}
                <circle className={styles.backdropDotHalo} r={radius * 3.1} />
                <circle className={styles.dot} r={radius} style={{ animationDelay: `${index * -0.33}s` }} />
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};
